import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import GmailGateModal from '@/components/GmailGateModal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';

// Helper function to safely parse JSON response
const safeJsonParse = async (response) => {
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    return { detail: text || 'Unknown error' };
  }
};

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  CalendarIcon, 
  Plus, 
  X, 
  Send,
  Link as LinkIcon,
  Search,
  Package,
  Sparkles,
  Loader2,
  ChevronDown,
  Save,
  FileText,
  Trash2,
  Mail,
  AlertTriangle,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { AudioBriefFields, AudioRefUploadButton, AudioRefRow } from '@/components/AudioTaskFields';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DRAFT_STORAGE_KEY = 'prism_task_draft';

const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];

const taskSchema = (isContent) => z.object({
  brand_name: z.string().min(1, 'Brand name is required'),
  brand_id: z.string().optional(),
  platform: z.string().min(1, 'Platform is required'),
  task_type: z.string().min(1, 'Task type is required'),
  dimensions: isContent ? z.string().optional() : z.string().min(1, 'Dimensions are required'),
  design_brief: z.string().min(10, isContent ? 'Brief must be at least 10 characters' : 'Design brief must be at least 10 characters'),
  references: z.array(z.object({
    type: z.enum(['link', 'text', 'audio']),
    value: z.string().min(1, 'Reference cannot be empty'),
    name: z.string().optional(),
  })).min(1, 'At least one reference is required'),
  required_by_date: z.date({ required_error: 'Required by date is needed' }),
  submitter_email: z.string().email('Please enter a valid email'),
  notification_emails: z.array(z.object({
    email: z.string().email('Please enter a valid email')
  })).min(1, 'At least one notification recipient is required'),
  cc_emails: z.array(z.object({
    email: z.string().email('Please enter a valid email').or(z.string().length(0))
  })).optional(),
  featured_products: isContent
    ? z.array(z.string()).optional()
    : z.array(z.string()).min(1, 'At least one product must be selected'),
  product_creatives: z.record(z.number()).optional() // For Meta: { productId: numberOfCreatives }
});

export default function TaskSubmissionPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const taskKind = location.pathname.startsWith('/submit/content') ? 'content'
    : location.pathname.startsWith('/submit/audio') ? 'audio' : 'design';
  // Audio follows the Content flow (no dimensions, optional products, mixed references).
  const isContent = taskKind !== 'design';
  const isAudio = taskKind === 'audio';
  const kindLabel = { design: 'Design', content: 'Content', audio: 'Audio' }[taskKind];
  const [audioDetails, setAudioDetails] = useState({});
  const [brands, setBrands] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [allSkus, setAllSkus] = useState([]);
  const [mastersLoaded, setMastersLoaded] = useState(false);
  const [platforms, setPlatforms] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nextTaskId, setNextTaskId] = useState('');
  const [skuSearch, setSkuSearch] = useState('');
  const [selectedSkus, setSelectedSkus] = useState([]);
  const [productCreatives, setProductCreatives] = useState({}); // { productId: numberOfCreatives }
  
  // Draft state
  const [hasDraft, setHasDraft] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  
  // Gmail integration status
  const [gmailConnected, setGmailConnected] = useState(null); // null = loading, true/false = status
  const [showGmailGate, setShowGmailGate] = useState(false);

  // As soon as we know Gmail is NOT connected, force the mandatory gate open.
  // Users cannot even see the form fields until Gmail is linked.
  useEffect(() => {
    if (gmailConnected === false) setShowGmailGate(true);
    else if (gmailConnected === true) setShowGmailGate(false);
  }, [gmailConnected]);

  const handleGmailConnected = () => {
    setGmailConnected(true);
    setShowGmailGate(false);
  };

  const { 
    register, 
    handleSubmit, 
    control,
    setValue,
    watch,
    formState: { errors } 
  } = useForm({
    resolver: zodResolver(taskSchema(isContent)),
    defaultValues: {
      brand_name: '',
      brand_id: '',
      platform: '',
      task_type: '',
      dimensions: '',
      design_brief: '',
      references: [{ type: 'link', value: '' }],
      required_by_date: undefined,
      submitter_email: user?.email || '',
      notification_emails: [{ email: '' }],
      cc_emails: [{ email: '' }],
      featured_products: []
    }
  });

  const selectedBrandId = watch('brand_id');
  const selectedBrandName = watch('brand_name');
  const selectedTaskType = watch('task_type');
  const selectedPlatform = watch('platform');
  
  // Check if Meta platform is selected (case-insensitive)
  const isMetaPlatform = selectedPlatform?.toLowerCase() === 'meta';
  
  // Ref to track prefill product (avoids effect dependency issues)
  const prefillProductRef = React.useRef(null);
  const skipNextClearRef = React.useRef(false);

  // Auto-populate dimensions when platform or task type changes
  useEffect(() => {
    const taskType = taskTypes.find(t => t.name === selectedTaskType);
    if (!taskType) return;
    
    if (selectedTaskType && selectedPlatform) {
      // Both task type and platform selected - try platform-specific first
      const platformDims = taskType.platform_dimensions?.[selectedPlatform];
      if (platformDims && platformDims.trim()) {
        setValue('dimensions', platformDims);
      } else if (taskType.dimensions && taskType.dimensions.trim()) {
        // Fallback to default task type dimensions
        setValue('dimensions', taskType.dimensions);
      } else {
        setValue('dimensions', '');
      }
    } else if (selectedTaskType) {
      // Only task type selected - check if any platform has dimensions, or use default
      if (taskType.dimensions && taskType.dimensions.trim()) {
        setValue('dimensions', taskType.dimensions);
      } else {
        // Check if there are any platform_dimensions
        const platformDims = taskType.platform_dimensions || {};
        const firstDims = Object.values(platformDims).find(d => d && d.trim());
        if (firstDims) {
          // Don't auto-fill platform-specific dims without platform selected
          setValue('dimensions', '');
        } else {
          setValue('dimensions', '');
        }
      }
    }
  }, [selectedPlatform, selectedTaskType, taskTypes, setValue]);

  // Filter products based on selected brand
  const filteredSkus = useMemo(() => {
    if (!selectedBrandName) return [];
    let skus = allSkus.filter(sku => sku.brand_name === selectedBrandName);
    if (skuSearch) {
      const search = skuSearch.toLowerCase();
      skus = skus.filter(sku => 
        sku.product_name.toLowerCase().includes(search)
      );
    }
    return skus;
  }, [allSkus, selectedBrandName, skuSearch]);

  // Clear selected SKUs when brand changes (but not on prefill)
  useEffect(() => {
    if (skipNextClearRef.current) {
      skipNextClearRef.current = false;
      // Apply prefill product
      if (prefillProductRef.current) {
        const product = prefillProductRef.current;
        prefillProductRef.current = null;
        // Use setTimeout to ensure state updates after this effect
        setTimeout(() => {
          setSelectedSkus([product]);
          setValue('featured_products', [product.id]);
        }, 0);
      }
      return;
    }
    setSelectedSkus([]);
    setValue('featured_products', []);
    setSkuSearch('');
  }, [selectedBrandName, setValue]);

  // Auto-fill notification recipient and CC when brand changes
  useEffect(() => {
    if (selectedBrandName) {
      const selectedBrand = brands.find(b => b.name === selectedBrandName);
      if (selectedBrand?.manager_email) {
        // Set brand manager as notification recipient
        setValue('notification_emails', [{ email: selectedBrand.manager_email }]);
      }
      // Default CC list intentionally left empty — users add their own recipients per task.
      setValue('cc_emails', []);
    }
  }, [selectedBrandName, brands, setValue]);

  // AI Rephrase state
  const [isRephrasing, setIsRephrasing] = useState(false);

  const handleRephrase = async (style = 'professional') => {
    const currentBrief = watch('design_brief');
    if (!currentBrief || currentBrief.trim().length < 10) {
      toast.error('Please enter at least 10 characters to rephrase');
      return;
    }

    setIsRephrasing(true);
    try {
      const response = await fetch(`${API_URL}/api/ai/rephrase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text: currentBrief, style })
      });

      const data = await safeJsonParse(response);
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to rephrase');
      }

      setValue('design_brief', data.rephrased_text);
      toast.success(`Rephrased in ${style} style`);
    } catch (error) {
      toast.error(error.message || 'Failed to rephrase text');
    } finally {
      setIsRephrasing(false);
    }
  };

  const { fields: refFields, append: appendRef, remove: removeRef, update: updateRef } = useFieldArray({
    control,
    name: 'references'
  });

  const { fields: emailFields, append: appendEmail, remove: removeEmail } = useFieldArray({
    control,
    name: 'notification_emails'
  });

  const { fields: ccFields, append: appendCc, remove: removeCc } = useFieldArray({
    control,
    name: 'cc_emails'
  });

  const selectedDate = watch('required_by_date');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [brandsRes, typesRes, nextIdRes, skusRes, platformsRes, gmailRes] = await Promise.all([
          fetch(`${API_URL}/api/masters/brands`, { credentials: 'include' }),
          fetch(`${API_URL}/api/masters/task-types?kind=${taskKind}`, { credentials: 'include' }),
          fetch(`${API_URL}/api/tasks/next-id`, { credentials: 'include' }),
          fetch(`${API_URL}/api/masters/skus`, { credentials: 'include' }),
          fetch(`${API_URL}/api/platforms`, { credentials: 'include' }),
          fetch(`${API_URL}/api/gmail/status`, { credentials: 'include' })
        ]);
        
        if (brandsRes.ok) setBrands(await brandsRes.json());
        if (typesRes.ok) setTaskTypes(await typesRes.json());
        if (nextIdRes.ok) {
          const data = await nextIdRes.json();
          setNextTaskId(data.next_task_id);
        }
        if (skusRes.ok) setAllSkus(await skusRes.json());
        if (platformsRes.ok) {
          const platformData = await platformsRes.json();
          // Platforms can be objects {name, icon_url} or strings
          setPlatforms(platformData.map(p => typeof p === 'string' ? p : p.name));
        }
        if (gmailRes.ok) {
          const gmailData = await gmailRes.json();
          setGmailConnected(gmailData.connected);
        } else {
          setGmailConnected(false);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setGmailConnected(false);
      } finally {
        setMastersLoaded(true);
      }
    };

    fetchData();
  }, [taskKind]);

  // Check for prefill data from Listing Quality page
  useEffect(() => {
    const prefillDataStr = sessionStorage.getItem('taskPrefill');
    if (!prefillDataStr) return;
    
    // Wait for data to load
    if (brands.length === 0 || taskTypes.length === 0 || allSkus.length === 0) return;
    
    try {
      const prefillData = JSON.parse(prefillDataStr);
      
      // Set flag to skip next clear
      skipNextClearRef.current = true;
      
      // Set brand
      if (prefillData.brand_name) {
        const matchingBrand = brands.find(b => b.name === prefillData.brand_name);
        setValue('brand_name', prefillData.brand_name);
        if (matchingBrand) {
          setValue('brand_id', matchingBrand.id);
        }
      }
      
      // Set task type - try to match element name to task type
      if (prefillData.element_name && taskTypes.length > 0) {
        let matchingType = taskTypes.find(t => 
          t.name.toLowerCase() === prefillData.element_name.toLowerCase()
        );
        if (!matchingType) {
          matchingType = taskTypes.find(t => 
            t.name.toLowerCase().includes(prefillData.element_name.toLowerCase()) ||
            prefillData.element_name.toLowerCase().includes(t.name.toLowerCase())
          );
        }
        setValue('task_type', matchingType ? matchingType.name : taskTypes[0].name);
      }
      
      // Set platform
      if (prefillData.platform) {
        setValue('platform', prefillData.platform);
      }
      
      // Store featured product in ref for later application
      if (prefillData.product_id) {
        const matchingSku = allSkus.find(s => s.id === prefillData.product_id);
        if (matchingSku) {
          prefillProductRef.current = matchingSku;
        }
      }
      
      // Clear prefill data
      sessionStorage.removeItem('taskPrefill');
      
      toast.success(`Pre-filled: ${prefillData.product_name} - ${prefillData.element_name}`);
    } catch (e) {
      console.error('Prefill error:', e);
      sessionStorage.removeItem('taskPrefill');
    }
  }, [brands, taskTypes, allSkus, setValue]);

  // ===== Copy-task prefill =====
  // When user clicks "Copy" on a submitted task in the Task Log we stash the full
  // task into sessionStorage under `taskCopyPrefill` and navigate the user to the
  // correct submission flow (/submit/design or /submit/content). This effect waits
  // for masters to load, then hydrates every field of the form from that payload.
  const copyPrefillAppliedRef = React.useRef(false);
  useEffect(() => {
    if (copyPrefillAppliedRef.current) return;
    const raw = sessionStorage.getItem('taskCopyPrefill');
    if (!raw) return;
    if (!mastersLoaded) return;

    try {
      const src = JSON.parse(raw);
      // Only apply if the prefill matches the current submission kind, to avoid
      // bleeding a design task into a content form and vice versa.
      const srcKind = src.task_kind || 'design';
      if (srcKind !== taskKind) {
        // Wrong flow — redirect once to the matching one, keeping the payload.
        if (!copyPrefillAppliedRef.current) {
          copyPrefillAppliedRef.current = true;
          navigate(`/submit/${['content', 'audio'].includes(srcKind) ? srcKind : 'design'}`, { replace: true });
        }
        return;
      }

      // Ensure the brand/sku/email auto-clear effect doesn't wipe our values
      skipNextClearRef.current = true;

      if (src.brand_name) {
        setValue('brand_name', src.brand_name);
        const matchingBrand = brands.find(b => b.name === src.brand_name);
        if (matchingBrand) setValue('brand_id', matchingBrand.id);
      }
      if (src.platform) setValue('platform', src.platform);
      if (src.task_type) setValue('task_type', src.task_type);
      if (src.dimensions != null) setValue('dimensions', src.dimensions || '');
      if (src.design_brief) setValue('design_brief', src.design_brief);

      // References — prefer the rich array; fall back to legacy reference_links
      let refs = [];
      if (Array.isArray(src.references) && src.references.length > 0) {
        refs = src.references
          .filter(r => r && (r.value || '').trim())
          .map(r => ({ type: ['text', 'audio'].includes(r.type) ? r.type : 'link', value: r.value, ...(r.name ? { name: r.name } : {}) }));
      } else if (Array.isArray(src.reference_links) && src.reference_links.length > 0) {
        refs = src.reference_links.filter(Boolean).map(v => ({ type: 'link', value: v }));
      }
      if (refs.length === 0) refs = [{ type: 'link', value: '' }];
      setValue('references', refs);

      if (Array.isArray(src.notification_emails) && src.notification_emails.length > 0) {
        setValue('notification_emails', src.notification_emails.map(email => ({ email })));
      }
      if (Array.isArray(src.cc_emails) && src.cc_emails.length > 0) {
        setValue('cc_emails', src.cc_emails.map(email => ({ email })));
      }
      if (src.remarks) setValue('remarks', src.remarks);
      if (src.audio_details) setAudioDetails(src.audio_details);
      if (src.assignee_id) setValue('assignee_id', src.assignee_id);
      if (src.assignee_name) setValue('assignee_name', src.assignee_name);
      if (src.required_by_date) {
        const d = new Date(src.required_by_date);
        if (!isNaN(d.getTime())) setValue('required_by_date', d);
      }
      if (src.expected_end_date) {
        const d = new Date(src.expected_end_date);
        if (!isNaN(d.getTime())) setValue('expected_end_date', d);
      }

      // Featured products — match by ID against allSkus
      if (Array.isArray(src.featured_products) && src.featured_products.length > 0) {
        const matchedSkus = allSkus.filter(s => src.featured_products.includes(s.id));
        if (matchedSkus.length > 0) {
          setSelectedSkus(matchedSkus);
          setValue('featured_products', matchedSkus.map(s => s.id));
        }
      }

      copyPrefillAppliedRef.current = true;
      sessionStorage.removeItem('taskCopyPrefill');
      toast.success(`Copied from ${src.task_id || 'task'} — review and submit`);
    } catch (e) {
      console.error('Copy-task prefill error:', e);
      sessionStorage.removeItem('taskCopyPrefill');
    }
  }, [brands, taskTypes, allSkus, mastersLoaded, setValue, taskKind, navigate]);

  useEffect(() => {
    if (user?.email) {
      setValue('submitter_email', user.email);
    }
  }, [user, setValue]);

  const handleSkuToggle = (sku) => {
    const isSelected = selectedSkus.some(s => s.id === sku.id);
    let newSelected;
    if (isSelected) {
      newSelected = selectedSkus.filter(s => s.id !== sku.id);
      // Remove from productCreatives if Meta
      if (isMetaPlatform) {
        setProductCreatives(prev => {
          const updated = { ...prev };
          delete updated[sku.id];
          return updated;
        });
      }
    } else {
      newSelected = [...selectedSkus, sku];
      // Initialize creatives count to 1 for Meta platform
      if (isMetaPlatform) {
        setProductCreatives(prev => ({ ...prev, [sku.id]: 1 }));
      }
    }
    setSelectedSkus(newSelected);
    setValue('featured_products', newSelected.map(s => s.id));
  };

  const removeSelectedSku = (skuId) => {
    const newSelected = selectedSkus.filter(s => s.id !== skuId);
    setSelectedSkus(newSelected);
    setValue('featured_products', newSelected.map(s => s.id));
    // Remove from productCreatives
    if (isMetaPlatform) {
      setProductCreatives(prev => {
        const updated = { ...prev };
        delete updated[skuId];
        return updated;
      });
    }
  };

  const updateProductCreatives = (skuId, count) => {
    const numCount = parseInt(count) || 1;
    setProductCreatives(prev => ({ ...prev, [skuId]: Math.max(1, numCount) }));
  };

  // Save form as draft to localStorage
  const saveDraft = () => {
    setSavingDraft(true);
    try {
      const formData = watch();
      const draftData = {
        ...formData,
        required_by_date: formData.required_by_date ? formData.required_by_date.toISOString() : null,
        selectedSkuIds: selectedSkus.map(s => s.id),
        productCreatives: productCreatives,
        savedAt: new Date().toISOString(),
        savedBy: user?.email
      };
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draftData));
      setHasDraft(true);
      setDraftLoaded(true);
      toast.success('Draft saved successfully');
    } catch (error) {
      toast.error('Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  // Clear draft from localStorage
  const clearDraft = () => {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
    setHasDraft(false);
    setDraftLoaded(false);
    toast.success('Draft cleared');
  };

  // Load draft from localStorage
  const loadDraft = (draft) => {
    try {
      // Set form values
      if (draft.brand_name) setValue('brand_name', draft.brand_name);
      if (draft.brand_id) setValue('brand_id', draft.brand_id);
      if (draft.platform) setValue('platform', draft.platform);
      if (draft.task_type) setValue('task_type', draft.task_type);
      if (draft.dimensions) setValue('dimensions', draft.dimensions);
      if (draft.design_brief) setValue('design_brief', draft.design_brief);
      if (draft.references) setValue('references', draft.references);
      else if (draft.reference_links) {
        // Migrate legacy draft format
        setValue('references', (draft.reference_links || []).map(l => ({ type: 'link', value: l.url || l.value || '' })));
      }
      if (draft.required_by_date) setValue('required_by_date', new Date(draft.required_by_date));
      if (draft.submitter_email) setValue('submitter_email', draft.submitter_email);
      if (draft.notification_emails) setValue('notification_emails', draft.notification_emails);
      if (draft.cc_emails) setValue('cc_emails', draft.cc_emails);
      
      // Set selected SKUs (need to match with loaded allSkus)
      if (draft.selectedSkuIds && draft.selectedSkuIds.length > 0) {
        const matchedSkus = allSkus.filter(sku => draft.selectedSkuIds.includes(sku.id));
        setSelectedSkus(matchedSkus);
        setValue('featured_products', matchedSkus.map(s => s.id));
      }
      
      // Set product creatives for Meta
      if (draft.productCreatives) {
        setProductCreatives(draft.productCreatives);
      }
      
      setDraftLoaded(true);
      toast.success('Draft loaded');
    } catch (error) {
      toast.error('Failed to load draft');
    }
  };

  // Check for existing draft on mount
  useEffect(() => {
    const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (savedDraft) {
      setHasDraft(true);
    }
  }, []);

  // Auto-save draft when user leaves the tab or navigates away
  useEffect(() => {
    const autoSaveDraft = () => {
      const formData = watch();
      // Only save if there's meaningful data (at least brand or design brief)
      if (formData.brand_name || (formData.design_brief && formData.design_brief.length > 5)) {
        const draftData = {
          ...formData,
          required_by_date: formData.required_by_date ? formData.required_by_date.toISOString() : null,
          selectedSkuIds: selectedSkus.map(s => s.id),
          productCreatives: productCreatives,
          savedAt: new Date().toISOString(),
          savedBy: user?.email,
          autoSaved: true
        };
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draftData));
        setHasDraft(true);
      }
    };

    // Save when tab becomes hidden (user switches tabs)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        autoSaveDraft();
      }
    };

    // Save before user leaves the page
    const handleBeforeUnload = () => {
      autoSaveDraft();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [watch, selectedSkus, productCreatives, user?.email]);

  // Load draft after master data is loaded
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!hasDraft || draftLoaded || brands.length === 0 || taskTypes.length === 0 || allSkus.length === 0) return;
    
    const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        loadDraft(draft);
      } catch (error) {
        console.error('Failed to parse draft:', error);
      }
    }
  }, [hasDraft, draftLoaded, brands, taskTypes, allSkus]);

  const onSubmit = async (data) => {
    // Hard-gate: Gmail must be connected before ANY task can be submitted.
    // Notifications, submitter emails, and follow-up mentions all rely on the
    // user's Gmail OAuth token — submitting without it silently strands the
    // task's stakeholders. Show a modal with a one-click path to Settings.
    if (gmailConnected !== true) {
      setShowGmailGate(true);
      return;
    }
    setIsSubmitting(true);
    try {
      const cleanRefs = (data.references || [])
        .filter(r => (r.value || '').trim())
        .map(r => ({ type: r.type || 'link', value: r.value.trim(), ...(r.name ? { name: r.name } : {}) }));

      const payload = {
        ...data,
        task_kind: taskKind,
        ...(isAudio ? { audio_details: Object.fromEntries(Object.entries(audioDetails).filter(([, v]) => v)) } : {}),
        dimensions: isContent ? '' : (data.dimensions || ''),
        required_by_date: format(data.required_by_date, 'yyyy-MM-dd'),
        references: cleanRefs,
        // Keep legacy `reference_links` populated from link-type refs so any older
        // downstream consumers continue to work seamlessly.
        reference_links: cleanRefs.filter(r => r.type === 'link').map(r => r.value),
        notification_emails: data.notification_emails
          ?.filter(e => e.email.trim())
          .map(e => e.email) || [],
        cc_emails: data.cc_emails
          ?.filter(e => e.email.trim())
          .map(e => e.email) || [],
        featured_products: selectedSkus.map(s => s.id),
        product_creatives: isMetaPlatform ? productCreatives : undefined
      };
      
      // Remove brand_id from payload as it's not needed in backend
      delete payload.brand_id;

      const response = await fetch(`${API_URL}/api/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await safeJsonParse(response);
        throw new Error(error.detail || 'Failed to submit task');
      }

      const result = await safeJsonParse(response);
      
      // Clear draft on successful submission
      localStorage.removeItem(DRAFT_STORAGE_KEY);
      setHasDraft(false);
      setDraftLoaded(false);
      
      toast.success(`Task ${result.task_id} submitted successfully!`);
      navigate('/tasks');
    } catch (error) {
      toast.error(error.message || 'Failed to submit task');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto animate-fade-in" data-testid={`task-submission-page-${taskKind}`}>
      {/* Blur/disable the form until Gmail is linked. Combined with the mandatory
          modal above, this makes the gate impossible to bypass. */}
      <div className={showGmailGate ? 'pointer-events-none select-none blur-sm opacity-60 transition-all' : 'transition-all'}>
      <div className="mb-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/submit')}
            className="w-9 h-9 rounded-full border border-slate-200 hover:border-slate-300 hover:bg-slate-50 flex items-center justify-center transition-colors"
            aria-label="Back to task type chooser"
            data-testid="back-to-chooser"
          >
            <ArrowRight className="h-4 w-4 text-slate-500 rotate-180" />
          </button>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={cn(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider',
                isAudio ? 'bg-orange-100 text-orange-700' : isContent ? 'bg-emerald-100 text-emerald-700' : 'bg-violet-100 text-violet-700'
              )}>
                {`${kindLabel} Task`}
              </span>
            </div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
              {`Submit New ${kindLabel} Task`}
            </h1>
            <p className="text-slate-500 mt-1">
              {isAudio ? 'Brief your team on a new audio requirement' : isContent ? 'Brief your team on a new content requirement' : 'Create a new design requirement request'}
            </p>
          </div>
        </div>
        {nextTaskId && (
          <div className="text-right">
            <p className="text-sm text-slate-500 mb-1">Next Task ID</p>
            <div className="inline-flex items-center px-4 py-2 bg-[#2EB4AC]/10 border border-[#2EB4AC]/30 rounded-lg">
              <span className="text-xl font-bold text-[#2EB4AC] animate-pulse">{nextTaskId}</span>
            </div>
            <p className="text-[10px] text-slate-400 mt-1">ID assigned on submit</p>
          </div>
        )}
      </div>

      {/* Draft Banner */}
      {draftLoaded && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between animate-in slide-in-from-top-2">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-amber-600" />
            <div>
              <p className="text-sm font-medium text-amber-800">Draft Restored</p>
              <p className="text-xs text-amber-600">Your previously saved draft has been loaded</p>
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={clearDraft}
            className="text-amber-700 border-amber-300 hover:bg-amber-100"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Discard Draft
          </Button>
        </div>
      )}

      {/* Gmail Integration Alert - Only show if NOT connected */}
      {gmailConnected === false && (
        <Link to="/settings?tab=integrations" className="block mb-6">
          <Alert className="bg-red-50 border-red-200 cursor-pointer hover:bg-red-100 transition-colors group">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-full">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                </div>
                <AlertDescription className="text-red-800">
                  <span className="font-semibold">Email notifications disabled!</span>
                  <span className="ml-1">Connect your Gmail in Control Panel → Integrations to send task notifications.</span>
                </AlertDescription>
              </div>
              <div className="flex items-center gap-2 text-red-600 group-hover:text-red-700">
                <span className="text-sm font-medium">Connect Now</span>
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Alert>
        </Link>
      )}

      {/* Gmail Connected Indicator */}
      {gmailConnected === true && (
        <div className="mb-6 flex items-center gap-2 text-sm text-green-600">
          <CheckCircle2 className="h-4 w-4" />
          <span>Email notifications enabled</span>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-lg">Task Details</CardTitle>
            <CardDescription>
              {`Fill in the ${kindLabel.toLowerCase()} requirement details below`}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Brand & Platform */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Brand Name *</Label>
                <Select 
                  value={selectedBrandName || ''} 
                  onValueChange={(value) => {
                    const brand = brands.find(b => b.name === value);
                    setValue('brand_name', value);
                    setValue('brand_id', brand?.id || '');
                  }}
                >
                  <SelectTrigger 
                    className="h-11 border-slate-200"
                    data-testid="brand-select"
                  >
                    <SelectValue placeholder="Select brand" />
                  </SelectTrigger>
                  <SelectContent>
                    {brands.map((brand) => (
                      <SelectItem key={brand.id} value={brand.name}>
                        {brand.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.brand_name && (
                  <p className="text-red-500 text-sm">{errors.brand_name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Platform *</Label>
                <Select 
                  value={watch('platform') || ''} 
                  onValueChange={(value) => setValue('platform', value)}
                >
                  <SelectTrigger 
                    className="h-11 border-slate-200"
                    data-testid="platform-select"
                  >
                    <SelectValue placeholder="Select platform" />
                  </SelectTrigger>
                  <SelectContent>
                    {platforms.map((platform) => (
                      <SelectItem key={platform} value={platform}>
                        {platform}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.platform && (
                  <p className="text-red-500 text-sm">{errors.platform.message}</p>
                )}
              </div>
            </div>

            {/* Task Type & Dimensions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Task Type *</Label>
                <Select 
                  value={selectedTaskType || ''} 
                  onValueChange={(value) => setValue('task_type', value)}
                >
                  <SelectTrigger 
                    className="h-11 border-slate-200"
                    data-testid="task-type-select"
                  >
                    <SelectValue placeholder="Select task type" />
                  </SelectTrigger>
                  <SelectContent>
                    {taskTypes.map((type) => (
                      <SelectItem key={type.id} value={type.name}>
                        <div className="flex items-center gap-2">
                          {type.name}
                          {type.dimensions && <Badge variant="secondary" className="text-[10px]">Has dimensions</Badge>}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.task_type && (
                  <p className="text-red-500 text-sm">{errors.task_type.message}</p>
                )}
                {/* Show editorial guidelines from the selected content task type */}
                {isContent && selectedTaskType && (() => {
                  const tt = taskTypes.find(t => t.name === selectedTaskType);
                  if (!tt?.guidelines) return null;
                  return (
                    <div className="mt-2 p-3 rounded-lg bg-emerald-50/60 border border-emerald-100" data-testid="task-type-guidelines-preview">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 mb-1.5">Editorial Guidelines</p>
                      <p className="text-[12.5px] text-slate-700 whitespace-pre-wrap leading-relaxed">{tt.guidelines}</p>
                    </div>
                  );
                })()}
              </div>

              {!isContent && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-700 font-medium">Dimensions *</Label>
                    {selectedTaskType && taskTypes.find(t => t.name === selectedTaskType)?.dimensions && (
                      <span className="text-xs text-[#2EB4AC] bg-[#2EB4AC]/10 px-2 py-0.5 rounded-full">Auto-filled</span>
                    )}
                  </div>
                  <Textarea
                    placeholder="Enter dimensions (one per line)&#10;e.g., 1500x1500 px"
                    className="min-h-[80px] border-slate-200 resize-none font-mono text-sm"
                    data-testid="dimensions-input"
                    {...register('dimensions')}
                  />
                  {errors.dimensions && (
                    <p className="text-red-500 text-sm">{errors.dimensions.message}</p>
                  )}
                </div>
              )}
            </div>

            {/* Featured Products */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-slate-700 font-medium">
                    {isContent ? 'Featured Products' : 'Featured Products *'}
                    {isContent && <span className="ml-1.5 text-xs text-slate-400 font-normal">(optional)</span>}
                  </Label>
                  <p className="text-sm text-slate-500">
                    {isMetaPlatform 
                      ? 'Select products and specify number of creatives required for each'
                      : selectedBrandName 
                        ? `Select SKUs for ${selectedBrandName}` 
                        : 'Select a brand first to see available products'}
                  </p>
                </div>
                {selectedSkus.length > 0 && (
                  <Badge variant="secondary" className="bg-[#2EB4AC]/10 text-[#2EB4AC]">
                    {selectedSkus.length} selected
                    {isMetaPlatform && ` • ${Object.values(productCreatives).reduce((a, b) => a + b, 0)} total creatives`}
                  </Badge>
                )}
              </div>

              {/* Selected products as tags */}
              {selectedSkus.length > 0 && (
                <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  {selectedSkus.map((sku) => (
                    <div 
                      key={sku.id} 
                      className={`flex items-center gap-2 bg-white border border-slate-200 rounded-lg ${isMetaPlatform ? 'px-3 py-2' : 'pl-3 pr-1 py-1'}`}
                    >
                      <span className="text-sm text-slate-700">{sku.product_name}</span>
                      {isMetaPlatform && (
                        <div className="flex items-center gap-1 border-l border-slate-200 pl-2">
                          <span className="text-xs text-slate-500">Creatives:</span>
                          <Input
                            type="number"
                            min="1"
                            value={productCreatives[sku.id] || 1}
                            onChange={(e) => updateProductCreatives(sku.id, e.target.value)}
                            className="w-14 h-7 text-center text-sm p-1"
                          />
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => removeSelectedSku(sku.id)}
                        className="p-1 hover:bg-slate-100 rounded"
                      >
                        <X className="h-3 w-3 text-slate-400 hover:text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Product Selection */}
              {selectedBrandName && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  {/* Search */}
                  <div className="p-3 border-b border-slate-200 bg-slate-50">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Search product name..."
                        value={skuSearch}
                        onChange={(e) => setSkuSearch(e.target.value)}
                        className="pl-10 h-10 border-slate-200"
                        data-testid="product-search"
                      />
                    </div>
                  </div>

                  {/* Product List */}
                  <div className="max-h-[200px] overflow-y-auto custom-scrollbar">
                    {filteredSkus.length === 0 ? (
                      <div className="p-6 text-center text-slate-500">
                        <Package className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                        <p>{skuSearch ? 'No products match your search' : 'No products available for this brand'}</p>
                        <p className="text-xs mt-1">Add products in Settings → Products</p>
                      </div>
                    ) : (
                      filteredSkus.map((sku) => {
                        const isSelected = selectedSkus.some(s => s.id === sku.id);
                        return (
                          <div
                            key={sku.id}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleSkuToggle(sku);
                            }}
                            className={cn(
                              "flex items-center gap-3 p-3 cursor-pointer border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors",
                              isSelected && "bg-[#2EB4AC]/5"
                            )}
                          >
                            <div 
                              className={cn(
                                "w-4 h-4 rounded border flex items-center justify-center flex-shrink-0",
                                isSelected 
                                  ? "bg-[#2EB4AC] border-[#2EB4AC]" 
                                  : "border-slate-300 bg-white"
                              )}
                            >
                              {isSelected && (
                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            <p className="font-medium text-slate-900">{sku.product_name}</p>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Show featured products error */}
            {errors.featured_products && (
              <p className="text-red-500 text-sm -mt-2">{errors.featured_products.message}</p>
            )}

            {/* Design Brief with AI Rephrase */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-slate-700 font-medium">{`${kindLabel} Brief *`}</Label>
                <div className="flex items-center gap-2">
                  <span
                    className="hidden sm:inline-flex items-center gap-1 text-[10px] font-medium text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5"
                    title="AI Rephrase runs on Groq · openai/gpt-oss-20b"
                    data-testid="rephrase-model-chip"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-[#2EB4AC]" />
                    Powered by Groq · GPT-OSS 20B
                  </span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isRephrasing}
                        className="text-[#2EB4AC] border-[#2EB4AC]/30 hover:bg-[#2EB4AC]/10 hover:text-[#2EB4AC]"
                        data-testid="ai-rephrase-btn"
                      >
                        {isRephrasing ? (
                          <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                        ) : (
                          <Sparkles className="h-4 w-4 mr-1.5" />
                        )}
                        AI Rephrase
                        <ChevronDown className="h-3 w-3 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onClick={() => handleRephrase('professional')} className="cursor-pointer">
                        <div>
                          <p className="font-medium">Professional</p>
                          <p className="text-xs text-slate-500">Clear & business-like</p>
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleRephrase('concise')} className="cursor-pointer">
                        <div>
                          <p className="font-medium">Concise</p>
                          <p className="text-xs text-slate-500">Short & to-the-point</p>
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleRephrase('detailed')} className="cursor-pointer">
                        <div>
                          <p className="font-medium">Detailed</p>
                          <p className="text-xs text-slate-500">More clarity & depth</p>
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleRephrase('creative')} className="cursor-pointer">
                        <div>
                          <p className="font-medium">Creative</p>
                          <p className="text-xs text-slate-500">Engaging & dynamic</p>
                        </div>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              <Textarea
                placeholder={`Describe the ${kindLabel.toLowerCase()} requirements in detail...`}
                className="min-h-[120px] border-slate-200 resize-none"
                data-testid="design-brief-input"
                {...register('design_brief')}
              />
              {errors.design_brief && (
                <p className="text-red-500 text-sm">{errors.design_brief.message}</p>
              )}
            </div>

            {/* References — links for Design, mixed link/text for Content */}
            {isAudio && <AudioBriefFields value={audioDetails} onChange={setAudioDetails} />}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-slate-700 font-medium">
                  {isContent ? 'References *' : 'Reference Links *'}
                </Label>
                <div className="flex items-center gap-1.5">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => appendRef({ type: 'link', value: '' })}
                    className="text-[#2EB4AC] hover:text-[#25908A]"
                    data-testid="add-link-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Link
                  </Button>
                  {isContent && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => appendRef({ type: 'text', value: '' })}
                      className="text-emerald-700 hover:text-emerald-800"
                      data-testid="add-text-btn"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Text
                    </Button>
                  )}
                  {isAudio && (
                    <AudioRefUploadButton onUploaded={(ref) => {
                      const refs = watch('references') || [];
                      const emptyIdx = refs.findIndex(r => !(r.value || '').trim());
                      if (emptyIdx >= 0) updateRef(emptyIdx, ref); else appendRef(ref);
                    }} />
                  )}
                </div>
              </div>
              {refFields.map((field, index) => {
                const currentType = watch(`references.${index}.type`) || 'link';
                return (
                  <div key={field.id} className="space-y-1.5">
                    <div className="flex gap-2 items-start">
                      {/* Toggle pill (only for content tasks) */}
                      {isContent && currentType !== 'audio' && (
                        <div className="inline-flex bg-slate-100 rounded-md p-0.5 h-11 items-center shrink-0">
                          <button
                            type="button"
                            onClick={() => updateRef(index, { type: 'link', value: watch(`references.${index}.value`) || '' })}
                            className={cn(
                              'flex items-center gap-1 px-2.5 h-9 rounded text-xs font-medium transition-colors',
                              currentType === 'link' ? 'bg-white text-violet-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                            )}
                            data-testid={`ref-toggle-link-${index}`}
                          >
                            <LinkIcon className="h-3.5 w-3.5" /> Link
                          </button>
                          <button
                            type="button"
                            onClick={() => updateRef(index, { type: 'text', value: watch(`references.${index}.value`) || '' })}
                            className={cn(
                              'flex items-center gap-1 px-2.5 h-9 rounded text-xs font-medium transition-colors',
                              currentType === 'text' ? 'bg-white text-emerald-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                            )}
                            data-testid={`ref-toggle-text-${index}`}
                          >
                            <FileText className="h-3.5 w-3.5" /> Text
                          </button>
                        </div>
                      )}

                      <div className="relative flex-1">
                        {currentType === 'audio' ? (
                          <AudioRefRow name={watch(`references.${index}.name`)} url={watch(`references.${index}.value`)} testId={`reference-audio-${index}`} />
                        ) : currentType === 'text' ? (
                          <Textarea
                            placeholder="Notes, talking points, or any reference content…"
                            className="min-h-[88px] border-slate-200"
                            data-testid={`reference-text-${index}`}
                            {...register(`references.${index}.value`)}
                          />
                        ) : (
                          <>
                            <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                            <Input
                              placeholder="https://example.com/reference"
                              className="pl-10 h-11 border-slate-200"
                              data-testid={`reference-link-${index}`}
                              {...register(`references.${index}.value`)}
                            />
                          </>
                        )}
                      </div>

                      {refFields.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeRef(index)}
                          className="text-slate-400 hover:text-red-500 shrink-0"
                          data-testid={`reference-remove-${index}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    {errors.references?.[index]?.value && (
                      <p className="text-red-500 text-xs pl-1">{errors.references[index].value.message}</p>
                    )}
                  </div>
                );
              })}
            </div>
            {errors.references && !Array.isArray(errors.references) && (
              <p className="text-red-500 text-sm">{errors.references.message || 'At least one reference is required'}</p>
            )}

            {/* Required By Date */}
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Required By Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full md:w-[280px] h-11 justify-start text-left font-normal border-slate-200",
                      !selectedDate && "text-slate-400"
                    )}
                    data-testid="date-picker-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {selectedDate ? format(selectedDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => setValue('required_by_date', date)}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              {errors.required_by_date && (
                <p className="text-red-500 text-sm">{errors.required_by_date.message}</p>
              )}
            </div>

            {/* Submitter Email */}
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Submitter Email *</Label>
              <Input
                type="email"
                className="h-11 border-slate-200"
                data-testid="submitter-email-input"
                {...register('submitter_email')}
              />
              {errors.submitter_email && (
                <p className="text-red-500 text-sm">{errors.submitter_email.message}</p>
              )}
            </div>

            {/* Notification Emails */}
            <div className="border-2 border-dashed border-teal-300 rounded-lg p-4 bg-teal-50/30 space-y-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-slate-700 font-medium">Notification Recipients *</Label>
                    <p className="text-sm text-slate-500">These emails will be notified on submission</p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => appendEmail({ email: '' })}
                    className="text-[#2EB4AC] hover:text-[#25908A]"
                    data-testid="add-email-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Email
                  </Button>
                </div>
                {emailFields.map((field, index) => (
                  <div key={field.id} className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="recipient@company.com"
                      className="h-11 border-slate-200"
                      data-testid={`notification-email-${index}`}
                      {...register(`notification_emails.${index}.email`)}
                    />
                    {emailFields.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeEmail(index)}
                        className="text-slate-400 hover:text-red-500"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              {errors.notification_emails && (
                <p className="text-red-500 text-sm">{errors.notification_emails.message || 'At least one notification recipient is required'}</p>
              )}

              {/* CC Emails */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-slate-700 font-medium">CC Recipients</Label>
                    <p className="text-sm text-slate-500">These emails will be CC'd on the notification</p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => appendCc({ email: '' })}
                    className="text-[#2EB4AC] hover:text-[#25908A]"
                    data-testid="add-cc-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add CC
                  </Button>
                </div>
                {ccFields.map((field, index) => (
                  <div key={field.id} className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="cc@company.com"
                      className="h-11 border-slate-200"
                      data-testid={`cc-email-${index}`}
                      {...register(`cc_emails.${index}.email`)}
                    />
                    {ccFields.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeCc(index)}
                        className="text-slate-400 hover:text-red-500"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="mt-6 flex justify-between items-center">
          <Button
            type="button"
            variant="outline"
            onClick={saveDraft}
            disabled={savingDraft || isSubmitting}
            className="border-slate-300 hover:bg-slate-50"
            data-testid="save-draft-btn"
          >
            {savingDraft ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save as Draft
          </Button>
          <Button
            type={gmailConnected === true ? 'submit' : 'button'}
            onClick={gmailConnected === true ? undefined : (e) => { e.preventDefault(); setShowGmailGate(true); }}
            disabled={isSubmitting}
            className="bg-[#2EB4AC] hover:bg-[#25908A] text-white px-8 h-11"
            data-testid="submit-task-btn"
            title={gmailConnected === true ? '' : 'Connect Gmail in Integrations to enable task submission'}
          >
            {isSubmitting ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                {`Submit ${kindLabel} Task`}
              </>
            )}
          </Button>
        </div>
      </form>
      </div>

      <GmailGateModal open={showGmailGate} onOpenChange={setShowGmailGate} onConnected={handleGmailConnected} />
    </div>
  );
}
